import { ICameraVideoTrack, IMicrophoneAudioTrack } from "agora-rtc-react";
import { Track, VideoBandwidthProfileOptions } from "twilio-video";
import * as TwilioVideo from "twilio-video";
import { ReactNode } from "react";
import { channelTypes } from "./config";
import { AgoraUserType } from "./VideoProvider/Agora";
import { TwilioUserType } from "./VideoProvider/Twilio";

declare module "twilio-video" {
  // These help to create union types between Local and Remote VideoTracks
  interface LocalVideoTrack {
    isSwitchedOff: undefined;
    setPriority: undefined;
  }
}

declare global {
  interface Window {
    TwilioVideo: typeof TwilioVideo;
    twilioRoom: TwilioVideo.Room;
  }

  interface HTMLMediaElement {
    setSinkId?(sinkId: string): Promise<undefined>;
  }
}

export type AgoraTracks = [IMicrophoneAudioTrack, ICameraVideoTrack];

export interface TrackState {
  video: boolean;
  audio: boolean;
}

export interface TwilioSettings {
  trackSwitchOffMode: VideoBandwidthProfileOptions["trackSwitchOffMode"];
  dominantSpeakerPriority?: Track.Priority;
  bandwidthProfileMode: VideoBandwidthProfileOptions["mode"];
  maxAudioBitrate: string;
  contentPreferencesMode?: "auto" | "manual";
  clientTrackSwitchOffControl?: "auto" | "manual";
}

export type MediaType = "audio" | "video";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Callback = (...args: any[]) => void;

export type StringOrNumber = number | string;

export type UserID = StringOrNumber;

export type TwilioVideoTrack = TwilioVideo.LocalVideoTrack | TwilioVideo.RemoteVideoTrack;

export type ChangeDevice = (deviceId: string) => void;

export type ChannelType = typeof channelTypes[number];

export type RoomInformation = {
  accessToken: string;
  channelName: string;
  tokenType: ChannelType | "";
  uid: number;
  numberOfParticipants?: number;
};

export enum OnlineStatus {
  ONLINE = "ONLINE",
  OFFLINE = "OFFLINE",
}

export enum ThemeColorMode {
  LIGTH = "light",
  DARK = "dark",
}

export interface AuthorizationTokens {
  accessToken: string;
  refreshToken: string;
  expireIn: number;
}

export type UserFriendType = {
  blocked: boolean;
  creationDate: number;
  friendDetails: UserDetailsType;
  friendId: number;
  id: number;
  userId: number;
};

export enum NotificationStatus {
  STATUS_DECLINED = "STATUS_DECLINED",
  STATUS_SNOOZE = "STATUS_SNOOZE",
  STATUS_ACCEPT = "STATUS_ACCEPT",
  STATUS_PENDING = "STATUS_PENDING",
}

export enum NotificationsTypes {
  INVITATION = "NOTIFICATION_INVITE_TO_CONVERSATION",
  VIDEO_AUTHENTICATION = "EVENT_VIDEO_AUTHENTICATION",
  ONLINE_FRIENDS = "NOTIFICATION_USER_ONLINE_INVITE",
  USER_REGISTERED = "NOTIFICATION_USER_REGISTERED",
  NOTIFICATION_PARTICIPANT_LEFT_CHANNEL = "NOTIFICATION_PARTICIPANT_LEFT_CHANNEL",
}

export interface ReceivedInvitationType {
  id: number;
  senderId: number;
  reciverId: number;
  notificationData: {
    notificationType: NotificationsTypes;
    channelName: string;
    videoPlatform: string;
    userId: number;
    friendId: number;
  };
  invitationStatus: NotificationStatus;
  creationDate: number;
}

export enum Genders {
  MALE = "male",
  FEMALE = "female",
  OTHER = "other",
  NOTHING = "nothing",
}

export interface notificationData {
  invatationType: string;
  channelName: string;
  videoPlatform: string;
}

export interface Invitation {
  id: number;
  senderId: number;
  reciverId: number;
  notificationData: notificationData;
  invitationStatus: string;
  creationDate: number;
}

export interface InviteNotification {
  notificationType: NotificationsTypes;
  invitation: Invitation;
}

export type AppVersion = "ALPHA" | "BETA";

export interface UserDetailsType {
  userId: number;
  fullname: string;
  firstName: string;
  lastName: string;
  gender: Genders;
  education: string;
  birthDate: number;
  country: string;
  region: string;
  state: string;
  profilePhotoUrl: string;
  status: OnlineStatus;
  bio: string;
  interests: string[];
  appVersion: AppVersion;
  position: number;
  showOnboarding: boolean;
}

export interface UserPhoneEmail {
  phoneNumber: string;
  email: string;
}

export type VideoUser = AgoraUserType | TwilioUserType;

export enum AuthenticationScreenModes {
  PHONE_NUMBER = "PhoneNumber",
  OTP = "OTP",
}

export enum SelectColorMode {
  LIGHT = "light",
  DARK = "dark",
}

export enum SigninPages {
  LOGIN = "login",
  REGISTER = "register",
}

export enum ReportReason {
  FOUL_LANGUAGE = "FOUL_lANGUAGE",
  NUDITY = "NUDITY",
  CAMERA_NOT_ACTIVE = "CAMERA_NOT_ACTIVE",
  SOUND_NOT_ACTIVE = "SOUND_NOT_ACTIVE",
  NOT_COMMUNICATING = "NOT_COMMUNICATING",
  IMPOLITE = "IMPOLITE",
}

export enum RegistrationStatus {
  FINISH = "completed",
  NOT_FINISH = "notCompleted",
  LOADING = "loading",
}

export enum BetaStatus {
  NOT_FINISH = "notCompleted",
  FINISH = "completed",
  NOT_STARTED = "notStarted",
  LOADING = "loading",
}

export interface Option {
  value: StringOrNumber;
  label: ReactNode;
}

export enum AuthStatus {
  AUTHENTICATED = "authenticated",
  NOT_AUTHENTICATED = "notAuthenticated",
  LOADING = "loading",
}

export interface UserSpeakState {
  [userId: string]: { isSpeaking: boolean };
}

export interface UserTrackInfo {
  [SID: string]: UserSpeakState;
}

export interface UserSpeakStatistic {
  userId: number;
  spokeSeconds: number;
}

export interface SpeakingTime {
  startSpeaking: number;
  speechTime: number;
}

export interface Expressions {
  angry: number;
  disgusted: number;
  fearful: number;
  happy: number;
  neutral: number;
  sad: number;
  surprised: number;
}

export type ExpressionsKey = keyof Expressions;

export interface ParticipantsData {
  [id: number]: UserDetailsType;
}

export interface Interest {
  id: string;
  value: string;
}

export enum LinkInvitationType {
  IN_APP_USER_INVITATION = "IN_APP_USER_INVITATION",
  BETA_USER_INVITATION = "BETA_USER_INVITATION",
}

export interface PhoneBookUser {
  name: string[];
  tel: string[];
}

export interface FormattedPhoneBookUser {
  name: string;
  phoneNumber: string;
}
